// src/services/aeo/scanDraftMock.ts
//
// The scan draft sample server: the fixture engine (src/oura/newscan/fixtures)
// behind the same promises the live calls return, with network-like delays.
// scanDraft.ts only reaches it on a local dev run with the live switch off
// (flags.ts), so no sample business reaches a staging or production screen.
// DELETE once the /scan-drafts routes are live everywhere.
//
// Dev tools, in the browser console:
//   localStorage.__oura_scan_draft_mode__ = "slow"   every call waits 3 s ("error": every call fails)
//   window.__ouraScanDraftMode = "error"            the same, for this tab only, no reload needed
//   window.__ouraResetScanDrafts()                  forget every sample draft and Search Console connect
//
// Sample sites: samplephysio.my (clinic), samplesleep.my (online store, brand
// mode, shoppers only; Both asks for Online instead), samplesupps.com
// (ingredient brand, brand mode, sells to businesses, the US / Singapore /
// Malaysia; a changed market list redrafts from the markets kept),
// sampleglow.my (skin clinic with an online shop, Both),
// thepilatescollective.my (existing client, recipe update), riversidedental.co
// (existing client). A domain
// starting with "fail" fails its first draft. Online on any other site fails
// with "No Google searches in the samples for this site".
//
// Joined journey (fixtures/journeySamples.ts): samplesmile.my, samplekids.my,
// samplespine.my, samplesight.my and sampletea.my are signed clients in the
// onboarding sample store too. Once a form is in, the first read for that
// business drafts its First AI check (`purpose: "first_check"`); starting it
// moves the onboarding record to its first check, then the baseline.

import type {
    CreateScanDraftRequest,
    DecisionChoice,
    QuestionPatch,
    ScanDraft,
    ScanDraftAlternative,
    ScanDraftBusiness,
    ScanDraftDetected,
    ScanDraftFailure,
    ScanDraftListItem,
    ScanDraftResult,
    ScanDraftWrite,
    StartScanDraftRequest,
    StartScanDraftResult,
    StartedScanDraft,
} from "./scanDraftTypes";
import * as store from "@oura/newscan/fixtures/store";

type ScanDraftDevMode = "slow" | "error" | "normal";

const MODE_KEY = "__oura_scan_draft_mode__";

/** The dev switch: `window.__ouraScanDraftMode` wins over localStorage `__oura_scan_draft_mode__`. */
function scanDraftDevMode(): ScanDraftDevMode {
    let raw: unknown = null;
    try {
        raw = (window as unknown as { __ouraScanDraftMode?: unknown }).__ouraScanDraftMode ?? window.localStorage.getItem(MODE_KEY);
    } catch {
        raw = null;
    }
    return raw === "slow" ? "slow" : raw === "error" ? "error" : "normal";
}

const NORMAL_MS = 160;
const SLOW_MS = 3000;
/** The website read: four pages, about 0.6 s each. */
const READ_MS = 2400;

function wait(ms: number): Promise<void> {
    const mode = scanDraftDevMode();
    return new Promise((resolve) => window.setTimeout(resolve, mode === "slow" ? Math.max(ms, SLOW_MS) : ms));
}

const NETWORK: ScanDraftFailure = { ok: false, kind: "network", message: "Check your connection and try again." };

/* The sample server keeps its drafts for the tab, so a refresh resumes a draft
 * the way the live backend would. Storage off: drafts live until the refresh. */
const STORE_KEY = "oura.newscan.fixtures.v2";

function loadStore(): void {
    try {
        const raw = window.sessionStorage.getItem(STORE_KEY);
        if (raw) store.restoreFixtureStore(JSON.parse(raw) as store.FixtureSnapshot);
    } catch {
        // Unreadable or blocked: start empty.
    }
}

function saveStore(): void {
    try {
        window.sessionStorage.setItem(STORE_KEY, JSON.stringify(store.snapshotFixtureStore()));
    } catch {
        // Storage off: the drafts stay in memory only.
    }
}

loadStore();

/** Run one sample call after its delay, or fail it like a dropped request in error mode. */
async function serve<T>(run: () => ScanDraftResult<T>, ms = NORMAL_MS): Promise<ScanDraftResult<T>> {
    await wait(ms);
    if (scanDraftDevMode() === "error") return NETWORK;
    const result = run();
    saveStore();
    return result;
}

const ok = <T>(data: T): ScanDraftResult<T> => ({ ok: true, data });

try {
    (window as unknown as { __ouraResetScanDrafts?: () => void }).__ouraResetScanDrafts = () => {
        store.resetFixtureStore();
        saveStore();
    };
} catch {
    // No window (tests): nothing to register.
}

/**
 * The onboarding sample store's read of a business's First AI check draft
 * (`scan.draft` on the record), drafting it when the form is in. Saved like
 * any other call, so a refresh keeps it. Synchronous: the record is built in
 * the same read.
 */
export function firstCheckDraftFor(businessId: string): store.FirstCheckDraftSummary | null {
    const summary = store.firstCheckSummary(businessId);
    saveStore();
    return summary;
}

/** The onboarding sample store's prefill: a prospect pitched in this session (its outlets, buy mode, day). */
export function startedPitchPrefill(domain: string): ReturnType<typeof store.startedPitchPrefill> {
    return store.startedPitchPrefill(domain);
}

/** Question n of a business's first check run in this session, for the sample signature pick. */
export function firstCheckQuestion(businessId: string, n: number): string | null {
    return store.firstCheckQuestion(businessId, n);
}

/** The name of a prospect whose scan started in this session, which signing keeps. */
export function startedPitchName(domain: string): string | null {
    return store.startedPitchName(domain);
}

/** Sample tools ("AI check finishes"): every scan on this site started by `upTo` (ms) finishes now. */
export function finishScansFor(domain: string, upTo: number): void {
    store.finishRunsFor(domain, upTo);
    saveStore();
}

/** Sample tools: forget a business's First AI check drafts (the journey rewound before the form). */
export function forgetFirstCheckDrafts(businessId: string): void {
    store.forgetFirstChecks(businessId);
    saveStore();
}

export const scanDraftMock = {
    readSite: (domain: string): Promise<ScanDraftResult<ScanDraftDetected>> => serve(() => ok(store.readSite(domain)), READ_MS),
    findOpen: (where: { domain?: string; businessId?: string }): Promise<ScanDraftResult<ScanDraft | null>> =>
        serve(() => ok(store.findOpen(where))),
    listOpen: (): Promise<ScanDraftResult<ScanDraftListItem[]>> => serve(() => ok(store.listOpen())),
    create: (req: CreateScanDraftRequest): Promise<ScanDraftResult<{ draft: ScanDraft; reused: boolean }>> => serve(() => store.createDraft(req)),
    recipeOffer: (businessId: string): Promise<ScanDraftResult<{ changed: number } | null>> => serve(() => ok(store.recipeOffer(businessId))),
    get: (id: string): Promise<ScanDraftResult<ScanDraft>> => serve(() => store.getDraft(id)),
    patchBusiness: (id: string, version: number, patch: Partial<ScanDraftBusiness>): Promise<ScanDraftResult<ScanDraftWrite>> =>
        serve(() => store.patchBusiness(id, version, patch)),
    rebuild: (id: string, version: number, opts?: { refreshWords?: boolean; refreshDemand?: boolean }): Promise<ScanDraftResult<ScanDraftWrite>> =>
        serve(() => store.rebuild(id, version, opts)),
    patchQuestion: (id: string, version: number, n: number, patch: QuestionPatch): Promise<ScanDraftResult<ScanDraftWrite>> =>
        serve(() => store.patchQuestion(id, version, n, patch)),
    alternatives: (id: string, n: number): Promise<ScanDraftResult<ScanDraftAlternative[]>> => serve(() => store.listAlternatives(id, n)),
    acceptAlternative: (id: string, version: number, n: number, altId: string): Promise<ScanDraftResult<ScanDraftWrite>> =>
        serve(() => store.acceptAlternative(id, version, n, altId)),
    patchWord: (id: string, version: number, variable: string, value: string): Promise<ScanDraftResult<ScanDraftWrite>> =>
        serve(() => store.patchWord(id, version, variable, value)),
    answer: (id: string, version: number, decisionId: string, choice: DecisionChoice): Promise<ScanDraftResult<ScanDraftWrite>> =>
        serve(() => store.answerDecision(id, version, decisionId, choice)),
    answerPlan: (id: string, version: number, decisionId: string, values: string[]): Promise<ScanDraftResult<ScanDraftWrite>> =>
        serve(() => store.answerPlanList(id, version, decisionId, values)),
    setRecipe: (
        id: string,
        version: number,
        mode: "take_all" | "keep_all" | "choose_each",
        picks?: Record<number, "take" | "keep">,
    ): Promise<ScanDraftResult<ScanDraftWrite>> => serve(() => store.setRecipe(id, version, mode, picks)),
    undo: (id: string, version: number): Promise<ScanDraftResult<ScanDraftWrite>> => serve(() => store.undo(id, version)),
    approve: (id: string, version: number): Promise<ScanDraftResult<ScanDraftWrite>> => serve(() => store.approve(id, version)),
    reopen: (id: string, version: number): Promise<ScanDraftResult<ScanDraftWrite>> => serve(() => store.reopen(id, version)),
    start: (id: string, version: number, req: StartScanDraftRequest): Promise<ScanDraftResult<StartScanDraftResult>> =>
        serve(() => store.start(id, version, req), 600),
    listStarted: (): Promise<ScanDraftResult<StartedScanDraft[]>> => serve(() => ok(store.listStarted())),
    discard: (id: string, version: number): Promise<ScanDraftResult<null>> => serve(() => store.discard(id, version)),
    refreshSearch: (id: string): Promise<ScanDraftResult<ScanDraftWrite>> => serve(() => store.refreshSearch(id)),
    searchConsoleState: (
        domain: string,
    ): Promise<ScanDraftResult<{ status: "connected" | "not_connected" | "action_needed"; property: string | null }>> =>
        serve(() => ok(store.searchConsoleState(domain))),
    connectSearchConsole: (domain: string): Promise<ScanDraftResult<{ redirectUrl: string | null }>> =>
        serve(() => ok(store.connectSearchConsole(domain))),
};
